import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { ToggleOn, GoogleMicLogo } from '../../assets/images/UI-dashboard';
import { AudioRecorder } from '../../components/AudioRecorder/AudioRecorder';

import TextFieldInput from '../../components/UI/TextFieldInput';
import SegmentSelection from './components/SegmentSelection/SegmentSelection';
import SKUTable from './components/SKUTable/SKUTable';
import { IndiaMap } from '../../components/IndiaMap/IndiaMap';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchFilters, resetFilters } from '../../features/filters/filterSlice';
import { ScenarioFilters } from './components/ScenarioFilters/ScenarioFilters';
import {
  StyledHeaderGrid,
  StyledButtonGrid,
  StyledIconGrid,
  StyledTypography,
  StyledLeftColumn,
  StyledRightColumn,
  StyledIconImage,
  StyledSegmentSelectionGrid,
  StyledContainer,
  PriceNote,
  NoOfIntervention,
  ReviewScenarioBtnText,
} from './ScenarioCreation.Styled';
import CustomButton from '../../components/UI/CustomButton/CustomButton';
import { useNavigate } from 'react-router-dom';
import { ReviewTable } from './components/ReviewTable/ReviewTable';
import { fetchPerformanceTableDataThunk } from '../../features/performanceTable/performanceTableSlice';
import {
  fetchSegment,
  resetStatesWithAvailablePackSegments,
  setIsPackReplacable,
} from '../../features/packSegments/packSegmentsSlice';
import { RootState } from '../../state/store';
import {
  resetReviewTable,
  selectEnableReviewTableDialog,
  toggleEnableReviewTableBtn,
  toggleEnableReviewTableDialog,
} from './components/ReviewTable/reviewTableSlice';
import { setEnableAudioMappingTable } from '../../features/SpeechRecognition/audioSlice';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { getLatestYearMonth, setScenarioName } from '../../features/scenarioCreation/scenarioCreationSlice';
import {
  setNoOfInterventions,
  setSelectedAvailablePack,
  setSelectedSegment,
} from '../../components/FormComponent/formSlice';
import { resetInputKpiState } from '../../features/inputKpi/inputKpiSlice';
import { VerticalLine } from '../../layout/components/Header/Header.Styled';
import { fetchMetrics } from '../../features/filters/metricsSlice';
import { fetchMapMetrics } from '../../features/map/mapSlice';
import CustomDialogWrapper from '../../components/UI/CustomDialogWrapper/CustomDialogWrapper';
import AudioSuggestions from '../../components/AudioRecorder/AudioSuggestions';
import {
  setComingFromScreen,
  setScenariosForComparison,
  updateSelectedStatus,
} from '../../features/scenarioLibrary/scenarioLibrarySlice';
import DownloadEditableExcel from './components/UpdateBasePrice/UpdateBasePrice';

const ScenarioCreation = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [baseUpdateOpen,setBaseUpdateOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { enableReviewTableBtn } = useAppSelector((state: RootState) => state.reviewTable);
  const isReviewTableDialogEnabled = useAppSelector(selectEnableReviewTableDialog);
  const { noOfIntervention } = useAppSelector((state: RootState) => state.form);
  const { scenarioName, selectedPvpOrConjointToggle } = useAppSelector((state: RootState) => state.scenarioCreation);
  const { isEditScenario, selectedCategory } = useAppSelector((state: RootState) => state.scenarioLibrary);

  const handleSave = (name: string) => {
    dispatch(setScenarioName(name));
  };

  const handleOpenDialog = () => {
    dispatch(setEnableAudioMappingTable(true));
    dispatch(toggleEnableReviewTableDialog(false));
    setDialogOpen(true);
  };

  const handleReviewInputShow = (val: Boolean) => {
    if (enableReviewTableBtn) {
      dispatch(setEnableAudioMappingTable(false));
      dispatch(toggleEnableReviewTableDialog(val as boolean));
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleCloseBaseUpdate = () => {
    setBaseUpdateOpen(false);
  }

  const fetchDataSequentially = async () => {
    try {
      dispatch(showLoader());
      await dispatch(resetFilters());
      const filtersResponse = isEditScenario
        ? await dispatch(fetchFilters({ category: selectedCategory })).unwrap()
        : await dispatch(fetchFilters({})).unwrap();
      const metricsResponse = await dispatch(fetchMetrics());
      const payload = {
        category: filtersResponse.enabled_keys.category,
        metric: metricsResponse.payload[0],
      };
      await dispatch(fetchMapMetrics(payload));
      const payloadForTable = {
        bottler: filtersResponse.enabled_keys.bottler,
        state: filtersResponse.enabled_keys.state,
      };
      await dispatch(fetchPerformanceTableDataThunk(payloadForTable));
      await dispatch(getLatestYearMonth());
      const payloadForPackSegment = {
        bottler: filtersResponse.enabled_keys.bottler,
        state: filtersResponse.enabled_keys.state,
        category: filtersResponse.enabled_keys.category,
      };
      await dispatch(fetchSegment(payloadForPackSegment));

      dispatch(hideLoader());
    } catch (error) {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    if (!isEditScenario) {
      dispatch(setNoOfInterventions(0));
      dispatch(setSelectedAvailablePack(null));
      dispatch(resetInputKpiState());
      dispatch(toggleEnableReviewTableBtn(false));
      dispatch(setSelectedSegment(''));
      dispatch(resetStatesWithAvailablePackSegments());
      dispatch(setIsPackReplacable(false));
      dispatch(toggleEnableReviewTableDialog(false));
      dispatch(resetReviewTable());
    }
  }, []);

  useEffect(() => {
    fetchDataSequentially();
  }, [dispatch]);

  const navigate = useNavigate();

  const addFromLibrary = () => {
    dispatch(setComingFromScreen('scenarioCreation'));
    dispatch(updateSelectedStatus(['Draft']));
    dispatch(setScenariosForComparison([]));
    navigate('/scenario-library');
  };

  const handlePvpOrConjointToggle = () => {};

  return (
    <StyledContainer>
      <StyledHeaderGrid container>
        <Grid item>
          <TextFieldInput initialName={scenarioName} onSave={handleSave} />
        </Grid>
        <StyledButtonGrid item>
          <StyledIconGrid onClick={handleOpenDialog}>
            <StyledIconImage src={GoogleMicLogo} alt="toggle" />
            <StyledTypography>Speak</StyledTypography>
          </StyledIconGrid>
          <VerticalLine style={{ margin: '10px 10px 0px 0px', height: '20px' }} />
          <StyledIconGrid>
            <StyledTypography
              style={{
                opacity: selectedPvpOrConjointToggle === 'pvp' ? '60%' : '100%',
              }}
            >
              PVP
            </StyledTypography>
            <StyledIconImage
              onClick={handlePvpOrConjointToggle}
              style={{
                transform: selectedPvpOrConjointToggle === 'pvp' ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
              src={ToggleOn}
              alt="toggle"
            />
            <StyledTypography
              style={{
                opacity: selectedPvpOrConjointToggle === 'pvp' ? '100%' : '60%',
              }}
            >
              Conjoint
            </StyledTypography>
          </StyledIconGrid>
          <CustomButton onClick={addFromLibrary} style={{ marginTop: '5px' }}>
            Add From Library
          </CustomButton>
          <CustomButton onClick={()=>{
            setBaseUpdateOpen(true)
          }} style={{ marginTop: '5px',marginLeft:'5px' }}>
            Base Price Update
          </CustomButton>
        </StyledButtonGrid>
      </StyledHeaderGrid>
      <div style={{ display: 'flex' }}>
        <StyledLeftColumn>
          <ScenarioFilters onReset={fetchDataSequentially} />
          <Grid style={{ marginTop: '20px' }} item>
            <IndiaMap />
          </Grid>
        </StyledLeftColumn>
        <StyledRightColumn style={{ position: 'relative' }} item>
          {isReviewTableDialogEnabled && <ReviewTable onClose={() => handleReviewInputShow(false)} />}
          <Grid style={{ zIndex: 0 }}>
            <Grid>
              <SKUTable />
            </Grid>
            <StyledSegmentSelectionGrid>
              {!isReviewTableDialogEnabled && <SegmentSelection />}
            </StyledSegmentSelectionGrid>
            <Grid style={{ marginTop: '10px', marginBottom: '5px' }}>
              <PriceNote>
                Note: Price in <span style={{ fontWeight: 900 }}>Absolute</span> and Distribution in{' '}
                <span style={{ fontWeight: 900 }}>Incremental</span>
              </PriceNote>
            </Grid>
            {!isReviewTableDialogEnabled && (
              <Grid>
                <CustomButton
                  style={{
                    background: 'white',
                    color: enableReviewTableBtn ? '#DF2020' : '#7D7D7D',
                    border: enableReviewTableBtn ? '0.91px solid #DF2020' : '0.91px solid #7d7d7d',
                    height: '42.67px',
                    marginBottom: '2%',
                  }}
                  onClick={() => handleReviewInputShow(true)}
                  fullWidth
                >
                  <ReviewScenarioBtnText>Review Scenario Input</ReviewScenarioBtnText>
                  <NoOfIntervention
                    sx={{
                      background: enableReviewTableBtn ? '#DF2020' : '#7d7d7d',
                    }}
                  >
                    {noOfIntervention}
                  </NoOfIntervention>
                </CustomButton>
              </Grid>
            )}
          </Grid>
        </StyledRightColumn>
      </div>
      <CustomDialogWrapper
        open={dialogOpen}
        onClose={handleCloseDialog}
        width="890px"
        showSecondWrapper={true}
        secondWrapperWidth="300px"
        SecondComponent={AudioSuggestions}
      >
        <AudioRecorder onClose={handleCloseDialog} />
      </CustomDialogWrapper>

      <CustomDialogWrapper
        open={baseUpdateOpen}
        onClose={handleCloseBaseUpdate}
        width="50vw"
        height='50vh'
      >
        <DownloadEditableExcel modelState={handleCloseBaseUpdate}/>
      </CustomDialogWrapper>
    </StyledContainer>
  );
};

export default ScenarioCreation;
